import React, {useContext, useEffect, useMemo} from "react";
import GridSection from "../../../components/grid/GridSection.jsx";
import {useBrand} from "../../../utils/brands/branding.js";
import {FilterContext} from "../../../components/filters/DashboardFilterContextProvider.jsx";
import {campaignGridProfitabilityValues, channelMarketingProfitabilitySubtotal, useMarketingColumns} from "./channelGridColumnSetup.js";
import {ALWAYS_LAST_KEY} from "../../../utils/dataGridSorting.js";
import {useTranslation} from "react-i18next";
import {aggregateMarketingChannelTotal} from "../../../utils/datagridTotal.js";
import useUserSettings from "../../../utils/useUserSettings.js";
import DataGrid2 from "../../../components/datagrid/DataGrid2.jsx";
import {useRenderNumber} from "../../../api/utilities/useValueRenderers.js";
import {combineMediaSourceQualities} from "../../../utils/reportingMetricPresentation.js";

function summarizeChannelCostQuality(channelData, isMarketing) {
    const suffix = isMarketing ? "Gross" : "Net";
    const rows = Object.values(channelData?.adAccounts ?? {}).flatMap((account) => Object.values(account.campaigns ?? {}));
    const known = rows.some((row) => typeof row.purchaseCostQualityAvailable === "boolean"
        || typeof row[`purchaseCostComplete${suffix}`] === "boolean"
        || typeof row[`purchaseCostUnknown${suffix}`] === "number"
        || typeof row[`purchaseCostEstimated${suffix}`] === "number");

    return {
        known,
        available: rows.every((row) => row.purchaseCostQualityAvailable !== false),
        complete: !known || (rows.every((row) => row.purchaseCostQualityAvailable !== false)
            && rows.every((row) => row[`purchaseCostComplete${suffix}`] !== false)
            && rows.reduce((sum, row) => sum + Number(row[`purchaseCostUnknown${suffix}`] ?? 0), 0) === 0),
        unknown: rows.reduce((sum, row) => sum + Number(row[`purchaseCostUnknown${suffix}`] ?? 0), 0),
        estimated: rows.reduce((sum, row) => sum + Number(row[`purchaseCostEstimated${suffix}`] ?? 0), 0),
    };
}

export function ChannelGridSection({
                                       channel,
                                       channelData,
                                       gridType,
                                       metricContext,
                                       setChannelTotal,
                                       setModalCampaignRow
                                   }) {
    const {
        sourceRegister,
        campaignRegister,
        campaignsAllowListed,
        campaignSearchFilter,
        sourcesRemoved,
    } = useContext(FilterContext);

    const {t} = useTranslation();
    const brand = useBrand(channel.name);
    const columns = useMarketingColumns(gridType, undefined, metricContext);
    const [userSettings] = useUserSettings();
    const renderNumber = useRenderNumber();

    const isMarketing = metricContext === undefined ? userSettings?.isMarketing === true : metricContext === "marketing";
    const channelCostQuality = summarizeChannelCostQuality(channelData, isMarketing);
    const channelMediaSourceQuality = channelData?.mediaSourceQuality
        ?? combineMediaSourceQualities(Object.values(channelData?.adAccounts ?? {}).flatMap((account) => Object.values(account.campaigns ?? {})).map((campaign) => campaign.mediaSourceQuality));
    const partialProfitability = channelData?.[isMarketing ? "partialProfitabilityMarketing" : "partialProfitability"];
    const actionableUnknownCount = partialProfitability?.actionableUnknownLineCount;
    const unknownLineCount = partialProfitability?.unknownLineCount;
    const hasActionableUnknownCount = actionableUnknownCount !== null && actionableUnknownCount !== undefined
        && actionableUnknownCount !== "" && Number.isFinite(Number(actionableUnknownCount)) && Number(actionableUnknownCount) >= 0;
    const hasUnknownLineCount = unknownLineCount !== null && unknownLineCount !== undefined
        && unknownLineCount !== "" && Number.isFinite(Number(unknownLineCount)) && Number(unknownLineCount) >= 0;
    const zeroSaleUnknownCount = hasActionableUnknownCount && hasUnknownLineCount && Number(unknownLineCount) > Number(actionableUnknownCount)
        ? Number(unknownLineCount) - Number(actionableUnknownCount)
        : null;

    const costQualityExplanation = [
        !channelCostQuality.complete && t("reporting_lab.cost_quality.channel_incomplete", {defaultValue: "Pokud je částka nákupu zobrazena, jde jen o známý mezisoučet. Úplný náklad některých podkladů není ověřený, proto zisk a POAS nemusí být dostupné za celý kanál."}),
        hasActionableUnknownCount && Number(actionableUnknownCount) > 0 && t("reporting_lab.partial_profitability.actionable_lines", {count: renderNumber(actionableUnknownCount), defaultValue: "K doplnění nákupní ceny: {{count}}."}),
        zeroSaleUnknownCount > 0 && t("reporting_lab.partial_profitability.zero_sale_lines", {defaultValue: "Řádky s přesnou nulovou prodejní hodnotou nejsou součástí akčního výběru."}),
        channelCostQuality.estimated > 0 && t("reporting_lab.cost_quality.estimated_rows", {countText: renderNumber(channelCostQuality.estimated), defaultValue: "Odhadovaná nákupní cena: {{countText}} položek; jde o uložený odhad, nikoli účetně jisté historické ocenění."}),
    ].filter(Boolean).join(" ");

    // Build dataSources WITHOUT side-effects
    const dataSources = useMemo(() => {
        if (channelData == null) return [];

        return Object.values(channelData.adAccounts ?? {}).flatMap((adAccount) => {
            const rows = [];
            const includedCampaigns = [];
            let totalRow = {name: t("project.total"), [ALWAYS_LAST_KEY]: true, mediaSourceQuality: channelMediaSourceQuality};
            let foundAny = false;

            Object.values(adAccount.campaigns).forEach((campaign) => {
                let filteredOut = false;

                if (
                    campaignSearchFilter.length > 0 &&
                    !campaign.name.toLowerCase().includes(campaignSearchFilter.toLowerCase())
                ) {
                    filteredOut = true;
                }
                if (
                    campaignsAllowListed.length > 0 &&
                    !campaignsAllowListed.includes(campaign.remoteId)
                ) {
                    filteredOut = true;
                }

                if (!filteredOut) {
                    foundAny = true;
                    const displayCampaign = gridType === "MarketingProfitability"
                        ? campaignGridProfitabilityValues(campaign, isMarketing, channelData.snapshot?.complete === true)
                        : campaign;
                    rows.push(displayCampaign);
                    includedCampaigns.push(displayCampaign);
                }
            });

            if (!sourcesRemoved.includes(channel.name) && foundAny) {
                totalRow = aggregateMarketingChannelTotal(totalRow, includedCampaigns, columns);
                // A channel-wide partial can serve as its sole account's subtotal only when
                // there is exactly one account. Never repeat it across multiple account rows.
                const channelSubtotal = channelMarketingProfitabilitySubtotal(
                    channelData,
                    isMarketing && gridType === "MarketingProfitability",
                    Object.keys(channelData.adAccounts ?? {}).length,
                    includedCampaigns.length === Object.values(channelData.adAccounts ?? {})
                        .reduce((count, account) => count + Object.keys(account.campaigns ?? {}).length, 0),
                );
                if (channelSubtotal) Object.assign(totalRow, channelSubtotal);
                rows.push(totalRow);
                return [
                    {
                        name: adAccount.name,
                        remoteId: adAccount.remoteId,
                        rows
                    }
                ];
            }
            return [];
        });
    }, [
        channelData,
        channelMediaSourceQuality,
        sourcesRemoved,
        campaignsAllowListed,
        campaignSearchFilter,
        userSettings,
        metricContext,
        columns,
        t
    ]);

    // After render, register campaigns & sources and pass totals upward
    useEffect(() => {
        // 1) Register each campaign and its integrationName
        dataSources.forEach((ds) => {
            ds.rows.forEach((row) => {
                if (!row[ALWAYS_LAST_KEY]) {
                    campaignRegister(row);
                    sourceRegister(row.integrationName);
                }
            });
        });

        // 2) Compute totals map and update parent
        const totalsMap = {};
        dataSources.forEach((ds) => {
            const last = ds.rows[ds.rows.length - 1];
            if (last && last[ALWAYS_LAST_KEY]) {
                totalsMap[`${channel.id}:${ds.remoteId}`] = last;
            }
        });
        setChannelTotal((prev) => ({
            ...Object.fromEntries(
                Object.entries(prev).filter(([key]) => !key.startsWith(`${channel.id}:`))
            ),
            ...totalsMap,
        }));
    }, [channel.id, dataSources, campaignRegister, sourceRegister, setChannelTotal]);

    // No data
    if (dataSources.length === 0) {
        return null;
    }

    // Render grids
    const calculateTitle = (channel, ds) => {
        if (channel.name === ds.name) return brand.name;
        if (ds.name.startsWith(brand.name)) return ds.name;
        return brand.name + " | " + ds.name;
    };


    return <>
        {dataSources.map((ds) => (
            <GridSection
                brand={brand}
                title={calculateTitle(channel, ds)}
                titleContext={gridType === "MarketingProfitability" && channelCostQuality.known && (!channelCostQuality.complete || channelCostQuality.estimated > 0) ? costQualityExplanation : null}
                key={`${channel.id}-${ds.remoteId}`}
                gridType={gridType}
            >
                <DataGrid2
                    group="marketing-profitability"
                    dataSource={ds.rows}
                    columns={columns}
                    showHeader
                    hasTotal
                    onRowDoubleClick={setModalCampaignRow}
                />
            </GridSection>
        ))}
    </>;
}
